/**
 * Module dependencies.
 */

import { Button, IconButton, Type, icons } from 'src/components/core';
import { Fill, color, media, units } from '@untile/react-components';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { ifNotProp, ifProp, theme } from 'styled-tools';
import { useLocomotiveScroll } from 'react-locomotive-scroll';
import styled, { css } from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
  isUpdating: boolean;
  onClear: () => void;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(Fill)<{ isOpen: boolean }>`
  overflow: hidden;
  position: fixed;
  transition: visibility ${theme('animations.easeOutQuadTransition')};
  visibility: visible;
  z-index: ${theme('zIndex.filters')};

  ${ifNotProp(
    'isOpen',
    css`
      pointer-events: none;
      visibility: hidden;
    `
  )}
`;

/**
 * `Overlay` styled component.
 */

const Overlay = styled(Fill)<{ isOpen: boolean }>`
  background-color: ${color('black')};
  opacity: ${ifProp('isOpen', 0.4, 0)};
  transition: opacity ${theme('animations.easeOutQuadTransition')};

  ${media.max('ms')`
    display: none;
  `}
`;

/**
 * `Sidebar` styled component.
 */

const Sidebar = styled.div<{ isOpen: boolean }>`
  background-color: ${color('white')};
  bottom: 0;
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: ${units(6)} ${theme('grid.gutterMobile')}px ${units(4)};
  position: absolute;
  right: 0;
  top: 0;
  transform: translateX(100%);
  transition: transform ${theme('animations.easeOutQuadTransition')};
  width: 100%;

  ${media.min('ms')`
    padding: ${units(11)} ${units(8)} ${units(4)};
    width: ${units(63)};
  `}

  ${ifProp(
    'isOpen',
    css`
      transform: translateX(0);
      transition-delay: 0.2s;
    `
  )}
`;

/**
 * `Header` styled component.
 */

const Header = styled.div`
  align-items: start;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${units(4)};
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  flex: 1;
  margin-bottom: ${units(5)};
  overflow-y: auto;
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  flex: 1;
`;

/**
 * `Footer` styled component.
 */

const Footer = styled.div`
  align-items: center;
  display: grid;
  grid-gap: ${units(3)};
  grid-template-columns: repeat(2, max-content);
`;

/**
 * `BaseSidebar` component.
 */

const BaseSidebar = (props: Props): ReactElement => {
  const {
    children,
    className,
    isOpen,
    isUpdating,
    onClear,
    onClose,
    onSubmit,
    title
  } = props;

  const { translate } = useTranslate();
  const locomotiveScroll = useLocomotiveScroll();
  const [yPosition, setYPosition] = useState<number>();

  useEffect(() => {
    if (isOpen && locomotiveScroll?.scroll) {
      setYPosition(locomotiveScroll?.scroll.scroll.instance.scroll.y);
    }
  }, [isOpen, locomotiveScroll]);

  return (
    <Wrapper
      className={className}
      isOpen={isOpen}
      style={{
        top: yPosition
      }}
    >
      <Overlay
        isOpen={isOpen}
        onClick={onClose}
      />

      <Sidebar isOpen={isOpen}>
        <Header>
          <Type.H4>{title}</Type.H4>

          <IconButton
            icon={icons.close}
            iconSize={units(2)}
            onClick={onClose}
          />
        </Header>

        <ContentWrapper>
          <Content>{children}</Content>
        </ContentWrapper>

        <Footer>
          <Button
            isLoading={isUpdating}
            onClick={onSubmit}
            size={'medium'}
          >
            {translate('common:actions.showResults')}
          </Button>

          <Button
            colorTheme={'tertiary'}
            onClick={onClear}
            size={'medium'}
          >
            {translate('common:actions.clear')}
          </Button>
        </Footer>
      </Sidebar>
    </Wrapper>
  );
};

/**
 * Export `BaseSidebar` component.
 */

export default BaseSidebar;
